/**
 * Utilidades de exportación para tablas
 */

// Función para limpiar formato de moneda y dejar número plano
const cleanCurrencyFormat = (value) => {
  if (value === null || value === undefined) return '';
  const strValue = String(value);
  // Detectar si es un valor con formato de moneda (contiene $ o MXN)
  if (strValue.includes('$') || strValue.includes('MXN')) {
    // Remover símbolo de moneda, comas de miles y espacios
    const cleaned = strValue.replace(/[$MXN\s,]/g, '');
    // Verificar si es un número válido
    const num = parseFloat(cleaned);
    if (!isNaN(num)) {
      return num.toString();
    }
  }
  return strValue;
};

// Función para procesar valores para CSV
const processValueForCSV = (value) => {
  let processed = cleanCurrencyFormat(value);
  processed = processed.replace(/"/g, '""');
  if (processed.includes(',') || processed.includes('"') || processed.includes('\n')) {
    processed = `"${processed}"`;
  }
  return processed;
};

/**
 * Exporta datos de una tabla a Excel (formato CSV compatible con Excel)
 * @param {Array} data - Array de objetos con los datos a exportar
 * @param {Array} columns - Array de objetos {text, value} con las columnas a exportar
 * @param {String} title - Título del archivo (sin extensión)
 */
export function exportToExcel(data, columns, title = 'export') {
  if (!data || data.length === 0) {
    console.warn('No hay datos para exportar');
    return;
  }

  // Obtener los headers
  const headers = columns.map(col => col.text || col.value);
  const keys = columns.map(col => col.value);

  // Crear contenido CSV con BOM para Excel
  let csvContent = '\uFEFF'; // BOM para UTF-8 en Excel
  
  // Agregar headers
  csvContent += headers.map(h => processValueForCSV(h)).join(',') + '\n';
  
  // Agregar filas de datos
  data.forEach(row => {
    const values = keys.map(key => processValueForCSV(row[key]));
    csvContent += values.join(',') + '\n';
  });

  // Crear y descargar el archivo
  const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Exporta una tabla HTML a Excel
 * @param {HTMLTableElement} tableElement - Elemento de tabla HTML
 * @param {String} title - Título del archivo (sin extensión)
 */
export function exportTableToExcel(tableElement, title = 'export') {
  if (!tableElement) {
    console.warn('No se proporcionó elemento de tabla');
    return;
  }

  // Crear contenido HTML para Excel
  const htmlContent = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" 
          xmlns:x="urn:schemas-microsoft-com:office:excel" 
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta charset="utf-8">
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>${title}</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          table { border-collapse: collapse; }
          th, td { border: 1px solid #000; padding: 5px; }
          th { background-color: #f0f0f0; font-weight: bold; }
        </style>
      </head>
      <body>
        ${tableElement.outerHTML}
      </body>
    </html>
  `;

  const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Exporta datos de matriz (filas con múltiples columnas dinámicas) a Excel
 * @param {Object} options - Opciones de exportación
 * @param {Array} options.headers - Headers de columnas (primera fila)
 * @param {Array} options.subHeaders - Sub-headers (segunda fila, opcional)
 * @param {Array} options.rows - Array de arrays con los datos de cada fila
 * @param {String} options.title - Título del archivo
 */
export function exportMatrixToExcel({ headers, subHeaders, rows, title = 'export' }) {
  let csvContent = '\uFEFF'; // BOM para UTF-8 en Excel

  // Agregar headers
  if (headers && headers.length) {
    csvContent += headers.map(h => processValueForCSV(h)).join(',') + '\n';
  }

  // Agregar sub-headers
  if (subHeaders && subHeaders.length) {
    csvContent += subHeaders.map(h => processValueForCSV(h)).join(',') + '\n';
  }

  // Agregar filas de datos
  rows.forEach(row => {
    const values = row.map(value => processValueForCSV(value));
    csvContent += values.join(',') + '\n';
  });

  const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Exporta datos de matriz a PDF usando la ventana de impresión del navegador
 * @param {Object} options - Opciones de exportación
 * @param {String} options.title - Título del documento
 * @param {String} options.subtitle - Subtítulo opcional
 * @param {Array} options.headers - Headers de columnas (primera fila)
 * @param {Array} options.subHeaders - Sub-headers (segunda fila, opcional)
 * @param {Array} options.rows - Array de arrays con los datos de cada fila
 * @param {String} options.logoSrc - URL del logo izquierdo (opcional)
 * @param {String} options.appLogoSrc - URL del logo derecho de la aplicación (opcional)
 */
export function exportMatrixToPDF({ title = 'Documento', subtitle = '', headers, subHeaders, rows, logoSrc = '', appLogoSrc = '' }) {
  // Crear contenido de la tabla
  let tableHtml = '<table>';
  
  // Headers
  if (headers && headers.length) {
    tableHtml += '<thead><tr>';
    headers.forEach((h, idx) => {
      tableHtml += `<th class="${idx === 0 ? 'first-col' : ''}">${h}</th>`;
    });
    tableHtml += '</tr>';
    
    // Sub-headers
    if (subHeaders && subHeaders.length) {
      tableHtml += '<tr class="sub-header">';
      subHeaders.forEach((h, idx) => {
        tableHtml += `<th class="${idx === 0 ? 'first-col' : ''}">${h}</th>`;
      });
      tableHtml += '</tr>';
    }
    tableHtml += '</thead>';
  }
  
  // Body
  tableHtml += '<tbody>';
  rows.forEach(row => {
    tableHtml += '<tr>';
    row.forEach((cell, idx) => {
      tableHtml += `<td class="${idx === 0 ? 'first-col' : 'data-cell'}">${cell ?? ''}</td>`;
    });
    tableHtml += '</tr>';
  });
  tableHtml += '</tbody></table>';

  // Crear documento HTML para impresión
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${title}</title>
      <style>
        @page {
          size: landscape;
          margin: 10mm;
        }
        body {
          font-family: Arial, sans-serif;
          font-size: 10px;
          margin: 0;
          padding: 20px;
        }
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
          border-bottom: 2px solid #333;
          padding-bottom: 10px;
        }
        .header-left {
          display: flex;
          align-items: center;
        }
        .header-left img {
          max-height: 50px;
          margin-right: 20px;
        }
        .header-right img {
          max-height: 50px;
        }
        .header-text h1 {
          margin: 0;
          font-size: 18px;
          color: #333;
        }
        .header-text p {
          margin: 5px 0 0 0;
          color: #666;
          font-size: 12px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 9px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 6px 8px;
          text-align: center;
        }
        th {
          background-color: #f5f5f5;
          font-weight: bold;
          color: #333;
        }
        .sub-header th {
          background-color: #fafafa;
          font-weight: normal;
          font-size: 8px;
          color: #666;
        }
        .first-col {
          text-align: left;
          font-weight: bold;
          background-color: #fafafa;
        }
        .data-cell {
          text-align: right;
        }
        tbody tr:nth-child(even) {
          background-color: #fafafa;
        }
        tbody tr:hover {
          background-color: #f0f0f0;
        }
        .footer {
          margin-top: 20px;
          text-align: center;
          font-size: 8px;
          color: #999;
        }
        @media print {
          body { padding: 0; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-left">
          ${logoSrc ? `<img src="${logoSrc}" alt="Logo">` : ''}
          <div class="header-text">
            <h1>${title}</h1>
            ${subtitle ? `<p>${subtitle}</p>` : ''}
          </div>
        </div>
        <div class="header-right">
          ${appLogoSrc ? `<img src="${appLogoSrc}" alt="App Logo">` : ''}
        </div>
      </div>
      ${tableHtml}
      <div class="footer">
        Generado el ${new Date().toLocaleDateString('es-MX')} a las ${new Date().toLocaleTimeString('es-MX')}
      </div>
    </body>
    </html>
  `;

  // Abrir ventana de impresión
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = function() {
      printWindow.print();
    };
  } else {
    console.error('No se pudo abrir la ventana de impresión. Verifica que el navegador no esté bloqueando popups.');
  }
}
